import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Phone, Mail, MapPin, Droplet, Sparkles, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader } from "@/components/Loader";
import { StatusBadge } from "@/components/StatusBadge";
import { initials, fmtDate, fmtDateTime } from "@/lib/format";

function useList(endpoint, patientId, key) {
  return useQuery({
    queryKey: [key, patientId],
    queryFn: async () => {
      const all = (await api.get(endpoint)).data || [];
      return all.filter((x) => x.patient_id === patientId);
    },
  });
}

export function PatientDetail({ patient, onBack }) {
  const qc = useQueryClient();
  const [aiOpen, setAiOpen] = useState(false);
  const [brief, setBrief] = useState("");
  const [note, setNote] = useState("");

  const records = useList("/clinical-records", patient.id, "pd-records");
  const rx = useList("/prescriptions", patient.id, "pd-rx");
  const labs = useList("/lab-orders", patient.id, "pd-labs");
  const appts = useList("/appointments", patient.id, "pd-appts");

  const aiMutation = useMutation({
    mutationFn: async () => (await api.post("/ai/clinical-note", { brief, patient_name: patient.full_name, note_type: "SOAP", encounter_type: "consultation" })).data,
    onSuccess: (d) => { setNote(d.note || d.content || JSON.stringify(d)); },
    onError: (e) => toast.error(errMsg(e, "AI note generation unavailable")),
  });

  const Info = ({ icon: Icon, label, value }) => (
    <div className="flex items-center gap-2 text-sm">
      <Icon className="h-4 w-4 text-slate-400" />
      <span className="text-slate-500">{label}:</span>
      <span className="font-medium text-slate-800">{value || "—"}</span>
    </div>
  );

  const Section = ({ query, cols }) => {
    if (query.isLoading) return <Loader />;
    if (!query.data?.length) return <div className="py-10 text-center text-sm text-slate-400">No records.</div>;
    return (
      <div className="divide-y divide-slate-100">
        {query.data.map((r, i) => (
          <div key={r.id || i} className="flex items-center justify-between py-3">
            <div>
              <p className="text-sm font-medium text-slate-800">{cols.primary(r)}</p>
              <p className="text-xs text-slate-500">{cols.secondary(r)}</p>
            </div>
            {r.status && <StatusBadge value={r.status} />}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <button onClick={onBack} data-testid="patient-back" className="mb-4 flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-teal-700">
        <ArrowLeft className="h-4 w-4" /> Back to patients
      </button>

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-teal-100 font-display text-xl font-bold text-teal-700">{initials(patient.full_name)}</span>
            <div>
              <h1 className="font-display text-2xl font-bold text-slate-900">{patient.full_name}</h1>
              <p className="font-mono text-xs text-slate-400">{patient.patient_number || patient.id}</p>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                <span className="capitalize">{patient.gender}</span>
                {patient.dob && <span>DOB {fmtDate(patient.dob)}</span>}
                {patient.blood_group && <span className="flex items-center gap-1"><Droplet className="h-3 w-3" /> {patient.blood_group}</span>}
              </div>
            </div>
          </div>
          <Button data-testid="ai-note-button" variant="outline" className="gap-1.5 border-teal-200 text-teal-700 hover:bg-teal-50" onClick={() => setAiOpen(true)}>
            <Sparkles className="h-4 w-4" /> AI clinical note
          </Button>
        </div>
        <div className="mt-5 grid gap-2 border-t border-slate-100 pt-4 sm:grid-cols-2 lg:grid-cols-3">
          <Info icon={Phone} label="Phone" value={patient.phone} />
          <Info icon={Mail} label="Email" value={patient.email} />
          <Info icon={MapPin} label="Address" value={patient.address} />
        </div>
      </div>

      <Tabs defaultValue="clinical" className="mt-6">
        <TabsList data-testid="patient-tabs">
          <TabsTrigger value="clinical" data-testid="tab-clinical">Clinical</TabsTrigger>
          <TabsTrigger value="prescriptions" data-testid="tab-prescriptions">Prescriptions</TabsTrigger>
          <TabsTrigger value="labs" data-testid="tab-labs">Labs</TabsTrigger>
          <TabsTrigger value="appointments" data-testid="tab-appointments">Appointments</TabsTrigger>
        </TabsList>
        {[
          ["clinical", records, { primary: (r) => r.diagnosis || r.complaint || r.encounter_type || "Record", secondary: (r) => fmtDateTime(r.created_at) }],
          ["prescriptions", rx, { primary: (r) => `${r.drug || "Drug"} ${r.dosage || ""}`, secondary: (r) => `${r.frequency || ""} ${r.duration || ""}` }],
          ["labs", labs, { primary: (r) => r.test_name || "Test", secondary: (r) => r.specimen || fmtDateTime(r.created_at) }],
          ["appointments", appts, { primary: (r) => r.reason || "Appointment", secondary: (r) => `${r.provider || ""} · ${fmtDateTime(r.date)}` }],
        ].map(([val, query, cols]) => (
          <TabsContent key={val} value={val}>
            <div className="rounded-xl border border-slate-200 bg-white px-5 py-2 shadow-sm">
              <Section query={query} cols={cols} />
            </div>
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2 font-display"><Sparkles className="h-5 w-5 text-teal-700" /> AI Clinical Note</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Textarea data-testid="ai-brief" placeholder="Brief: e.g. 34yo male, 3-day fever, headache, no rash…" value={brief} onChange={(e) => setBrief(e.target.value)} rows={3} />
            <Button data-testid="ai-generate" onClick={() => aiMutation.mutate()} disabled={!brief || aiMutation.isPending} className="bg-teal-700 hover:bg-teal-800">
              {aiMutation.isPending ? "Generating…" : "Generate SOAP note"}
            </Button>
            {note && <Textarea data-testid="ai-output" value={note} onChange={(e) => setNote(e.target.value)} rows={10} className="font-mono text-xs" />}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAiOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
